import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import { query } from "svelte-apollo";
import { GET_STOCK_SITE_DETAILS } from "@modern/queries/stock_sites/stockSite";
import * as Sentry from "@sentry/browser";

// types
import type { Warehouse } from "@shared/types/Warehouse";

// stores
import warehouseStore from "@shared/svelte/Stores/WarehouseStore";

export const getWarehouseData = async (
  distributorId: number,
  skus: String[] = [],
  after: String = null,
  stockSiteId: String = null,
  forProductLine: boolean = true,
) => {
  try {
    const data = await HttpPromise({
      url: `/api/retailer/warehouses`,
      method: "GET",
      data: {
        distributor_id: distributorId,
        first: 5,
        skus,
        after,
        selected_stock_site_id: stockSiteId,
        for_product_line: forProductLine,
      },
    });

    const warehouses = data.selectedStockSite
      ? [data.selectedStockSite, ...data.pickupStockSites]
      : data.pickupStockSites;

    const result = {
      data: {
        pageInfo: data.pageInfo,
        defaultWarehouse: data.defaultStockSite,
        selectedWarehouse: data.selectedStockSite,
        warehouses,
      },
      loaded: true,
      failed: false,
    };

    warehouseStore.update(() => result);
    return result;
  } catch (_e) {
    warehouseStore.update(warehouseData => ({
      ...warehouseData,
      loaded: true,
      failed: true,
    }));
  }
};

type StockSite = {
  name: string;
  formattedName: string;
  formattedAddress: string;
  region: string;
  storeWebsite: string;
  storePhone: string;
  storeEmail: string;
  type: string;
  pickupEnabled: boolean;
};

type StockSiteLocationsQueryResult = {
  getStockSite: StockSite;
};

export const getOrderStockSite = async (
  distributorUsesWineCellar: boolean,
  distributorId: number,
  warehouseId: number,
  warehouseLocationCode: string,
): Promise<Warehouse> => {
  if (!distributorUsesWineCellar || !warehouseId || !warehouseLocationCode) return;

  const variables = {
    distributorId: distributorId.toString(),
    stockSiteId:
      warehouseLocationCode && warehouseLocationCode.startsWith("VIP_")
        ? warehouseLocationCode.substring(4)
        : warehouseLocationCode,
  };
  try {
    const res = query<StockSiteLocationsQueryResult>(GET_STOCK_SITE_DETAILS, { variables });
    const result = await res?.result();
    const stockSite = result?.data?.getStockSite;
    return stockSite
      ? {
          name: stockSite?.name,
          formatted_name: stockSite?.formattedName,
          formatted_address: stockSite?.formattedAddress,
          state: stockSite?.region,
          email: stockSite?.storeEmail,
          phone: stockSite?.storePhone,
          website: stockSite?.storeWebsite,
          distributor_id: distributorId,
          id: warehouseId,
          for_pickup: stockSite?.pickupEnabled,
        }
      : null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};
